import { createFileRoute } from "@tanstack/react-router";
import {
  Container,
  CtaBand,
  Eyebrow,
  Lead,
  PrimaryLink,
  Quote,
  TextLink,
} from "@/components/site/primitives";
import { SITE, TESTIMONIALS } from "@/lib/site-content";
import { ogImageMeta } from "@/lib/og";
import { PartOfHub, RelatedLinks } from "@/components/site/RelatedLinks";
import { breadcrumbMeta } from "@/lib/internal-links";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { contactFunnel, trackOutboundClick } from "@/lib/analytics";
import { AREAS, jsonLdScript, professionalServiceSchema, serviceSchema } from "@/lib/schema";

const PATH = "/fractional-cto-hong-kong";
const CTA_SOURCE_HERO = "fractional_cto_hong_kong_hero";
const CTA_SOURCE_BAND = "fractional_cto_hong_kong_footer";

const title = "Fractional CTO in Hong Kong | Timothy Ng, Scryla";
const description =
  "Outsourced build slipping and deadlines missed? Fractional CTO and technical advisor for Hong Kong founders and boards, working remote-first on HKT (GMT+8).";

const WHEN = [
  {
    title: "You are building with an outsourced team",
    body: "Development spread across Hong Kong, the mainland or further afield needs someone reading what was promised against what shipped. Without that, invoices get approved on trust.",
  },
  {
    title: "A full-time CTO is not yet justified",
    body: "The technical decisions are already executive-level even when the headcount is not. Fractional gives you the judgement without the salary, equity and hiring risk.",
  },
  {
    title: "Investors are about to look under the bonnet",
    body: "Technical diligence finds what it finds. Knowing it first, and having a plan for it, is worth more than a polished deck.",
  },
  {
    title: "The board cannot tell if delivery is on track",
    body: "When reporting is filtered through the people doing the work, the board needs an independent read on the roadmap, the team and the next twelve months of cost.",
  },
];

const WORKING = [
  {
    title: "Hong Kong hours, worked properly",
    body: "HKT is GMT+8 and Hong Kong does not observe daylight saving, so the gap is seven hours in UK summer and eight in winter. A 09:00 UK start puts me with you at 16:00 or 17:00 HKT, every working day, with earlier starts by arrangement when something is live.",
  },
  {
    title: "Remote by default",
    body: "Fixed weekly contact, board attendance by video, and written reports your team and your investors can both act on. Most technical advisory into the region already runs this way.",
  },
  {
    title: "On-site when it earns its place",
    body: "Some work needs a room: a board offsite, a team reset, a diligence week. I travel to Hong Kong where that is the right call, arranged and costed per engagement rather than assumed.",
  },
];

const FAQ = [
  {
    q: "Are you based in Hong Kong?",
    a: `No. I am based in ${SITE.location} and work with Hong Kong founders and boards on a remote-first basis, to your hours. I have family in Hong Kong and am over there from time to time, but I would rather say plainly where I am based than present a service-area page as a local office.`,
  },
  {
    q: "How does the time difference actually work?",
    a: "Hong Kong holds GMT+8 all year with no daylight saving, so the gap from the UK is seven hours between late March and late October and eight hours the rest of the year. That makes the late-afternoon HKT slot a fixed part of the week rather than something renegotiated each month, and personal data questions in scope, the Personal Data (Privacy) Ordinance, Cap. 486, overseen by the PCPD, get handled in that same standing slot rather than by email tennis.",
  },
  {
    q: "Will you travel to Hong Kong?",
    a: "Yes, where it changes the outcome, a board offsite, a delivery reset, a diligence week. Travel and on-site time are agreed and costed as part of the engagement up front, so there are no surprises on either side.",
  },
  {
    q: "What sectors do you know?",
    a: "Fintech, iGaming and gambling, digital health, SaaS, data and agencies. Those are sectors I have run technology and operations in as a CTO and COO, not sectors I have read about.",
  },
  {
    q: "Is this the same as a technology non-executive director?",
    a: "No, and I keep the two separate. A fractional CTO works inside the business, owning technical decisions and leading the team. A non-executive director sits outside it, holding the executive to account on the board's behalf. I will tell you which one you actually need.",
  },
];

export const Route = createFileRoute("/fractional-cto-hong-kong")({
  // Static marketing content: let the CDN serve it and revalidate in the background.
  headers: () => ({
    "cache-control": "public, max-age=0, s-maxage=3600, stale-while-revalidate=86400",
  }),
  head: () => ({
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "website" },
      { property: "og:url", content: `${SITE.url}${PATH}` },
      ...ogImageMeta("fractional-cto-hong-kong", "Fractional CTO for Hong Kong | Timothy Ng"),
    ],
    links: [{ rel: "canonical", href: `${SITE.url}${PATH}` }],
    scripts: [
      jsonLdScript(
        professionalServiceSchema({
          path: PATH,
          name: "Fractional CTO for Hong Kong | Timothy Ng, Scryla",
          areaServed: AREAS.hongKong,
          serviceType: "Fractional CTO",
        }),
      ),
      jsonLdScript(
        serviceSchema({
          path: PATH,
          serviceType: "Fractional CTO",
          areaServed: AREAS.hongKong,
        }),
      ),
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQ.map((item) => ({
            "@type": "Question",
            name: item.q,
            acceptedAnswer: { "@type": "Answer", text: item.a },
          })),
        }),
      },
      breadcrumbMeta(PATH),
    ],
  }),
  component: HongKongPage,
});

function HongKongPage() {
  return (
    <div>
      <section
        aria-labelledby="fctohk-hero-heading"
        className="border-b border-border bg-gradient-to-b from-background to-surface"
      >
        <Container>
          <Breadcrumbs path={PATH} className="mb-6" />
          <Eyebrow>Fractional CTO · Hong Kong</Eyebrow>
          <h1
            id="fctohk-hero-heading"
            className="mt-4 max-w-[22ch] text-[34px] leading-[1.06] font-bold tracking-[-0.035em] text-balance md:text-5xl"
          >
            Fractional CTO for Hong Kong founders.
          </h1>
          <Lead className="mt-6">
            Outsourced build slipping and invoices approved on trust? Senior technical leadership
            for founders and boards in Hong Kong, without a full-time executive hire. Former CTO and
            COO, and founder of my own consultancy since 2014, working on your hours rather than
            mine.
          </Lead>
          <PartOfHub hub="/fractional-cto-southeast-asia" className="mt-5" />
          <div className="mt-8 flex flex-wrap gap-3">
            <PrimaryLink to="/contact" onClick={() => contactFunnel.ctaClick(CTA_SOURCE_HERO)}>
              Arrange a conversation
            </PrimaryLink>
            <a
              href={SITE.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackOutboundClick("linkedin", CTA_SOURCE_HERO)}
              className="inline-flex items-center gap-2 rounded-lg border border-border-strong bg-background px-6 py-3.5 text-[15px] font-semibold transition-colors hover:border-primary hover:text-primary"
            >
              View my LinkedIn profile
            </a>
          </div>
        </Container>
      </section>

      <section className="border-b border-border" aria-labelledby="fctohk-when-heading">
        <Container>
          <Eyebrow>When founders call</Eyebrow>
          <h2
            id="fctohk-when-heading"
            className="mt-3 max-w-[26ch] text-2xl font-bold tracking-[-0.03em] md:text-[32px]"
          >
            Four situations I am usually brought into
          </h2>
          <ul className="mt-8 grid gap-5 md:grid-cols-2">
            {WHEN.map((item) => (
              <li key={item.title} className="rounded-lg border border-border bg-card p-6">
                <h3 className="text-lg font-semibold">{item.title}</h3>
                <p className="mt-2 text-[15px] leading-relaxed text-body">{item.body}</p>
              </li>
            ))}
          </ul>
          <RelatedLinks className="mt-7" paths={["/case-studies"]} />
        </Container>
      </section>

      <section className="border-b border-border bg-surface" aria-labelledby="fctohk-work-heading">
        <Container>
          <Eyebrow>How the engagement works</Eyebrow>
          <h2
            id="fctohk-work-heading"
            className="mt-3 max-w-[26ch] text-2xl font-bold tracking-[-0.03em] md:text-[32px]"
          >
            Working with a Hong Kong business from the UK
          </h2>
          <ul className="mt-8 grid gap-5 md:grid-cols-3">
            {WORKING.map((item) => (
              <li key={item.title} className="rounded-lg border border-border bg-card p-6">
                <h3 className="text-lg font-semibold">{item.title}</h3>
                <p className="mt-2 text-[15px] leading-relaxed text-body">{item.body}</p>
              </li>
            ))}
          </ul>
          <p className="mt-7 max-w-[62ch] text-[15px] leading-relaxed text-body">
            I take on a small number of clients at a time, have no development team to keep busy and
            no supplier referrals to protect. No lock-in either: when you are ready to bring the
            role in-house, I help you hire well and step back.
          </p>
        </Container>
      </section>

      <section className="border-b border-border" aria-labelledby="fctohk-quotes-heading">
        <Container>
          <h2 id="fctohk-quotes-heading" className="sr-only">
            What founders say
          </h2>
          <div className="grid gap-5 md:grid-cols-2">
            <Quote item={TESTIMONIALS.milanShah} />
            <Quote item={TESTIMONIALS.steveKellyShort} />
          </div>
        </Container>
      </section>

      <section className="border-b border-border bg-surface" aria-labelledby="fctohk-faq-heading">
        <Container>
          <Eyebrow>Questions</Eyebrow>
          <h2
            id="fctohk-faq-heading"
            className="mt-3 text-2xl font-bold tracking-[-0.03em] md:text-[32px]"
          >
            Hong Kong FAQs
          </h2>
          <dl className="mt-8 divide-y divide-border overflow-hidden rounded-lg border border-border bg-card">
            {FAQ.map((item) => (
              <div key={item.q} className="px-6 py-6">
                <dt className="text-lg font-semibold text-ink">{item.q}</dt>
                <dd className="mt-2 max-w-[70ch] text-[15px] leading-relaxed text-body">
                  {item.a}
                </dd>
              </div>
            ))}
          </dl>
          <RelatedLinks
            className="mt-7"
            paths={["/fractional-cto-southeast-asia", "/fractional-cto-vs-full-time-vs-agency"]}
          />
        </Container>
      </section>

      <CtaBand
        heading="Building in Hong Kong?"
        body="Tell me about the business, the stage it is at, and what is worrying you. I will tell you plainly whether I am the right person, and what I would look at first."
        to="/contact"
        cta="Arrange a conversation"
        onCtaClick={() => contactFunnel.ctaClick(CTA_SOURCE_BAND)}
      />
    </div>
  );
}
